import "../css/subpage.less";
import React, { useState, useRef } from "react";
import {
  Button,
  Checkbox,
  Form,
  FormProps,
  Input,
  Modal,
  Radio,
  Space,
} from "antd";
import { useTranslation } from "react-i18next";
import PageMeta from "../common/PageMeta";
import SubpageTitle from "../common/SubpageTitle";
import PrivacyPolicyContent from "./PrivacyPolicyContent";
import RecaptchaComponent from "../common/RecaptchaComponent";
import FaqStickyLink from "../common/FaqStickyLink";
import { PAGE_META } from "../seo/pageMeta";

const INQUIRY_TYPES = [
  { value: "quote", labelKey: "inquiry.type_quote" },
  { value: "consultation", labelKey: "inquiry.type_consultation" },
  { value: "recruitment", labelKey: "inquiry.type_recruitment" },
] as const;

type InquiryTypeValue = (typeof INQUIRY_TYPES)[number]["value"];

type FieldType = {
  companyName?: string;
  departmentName?: string;
  name?: string;
  mail?: string;
  telephone?: string;
  inquiryType?: InquiryTypeValue;
  remarks?: string;
  privacyAgree?: boolean;
  captchaToken?: string;
};

function buildMailContent(values: FieldType, t: (key: string) => string): string {
  const typeEntry = INQUIRY_TYPES.find((item) => item.value === values.inquiryType);
  const typeLabel = typeEntry ? t(typeEntry.labelKey) : String(values.inquiryType ?? "");
  const parts = [`【${t("inquiry.type")}】${typeLabel}`];
  if (values.remarks?.trim()) {
    parts.push(`【${t("inquiry.remarks")}】\n${values.remarks.trim()}`);
  }
  return parts.join("\n\n");
}

function RequiredLabel({ children }: { children: React.ReactNode }) {
  const { t } = useTranslation();
  return (
    <span className="inquiry-form__label-text">
      <span className="inquiry-form__badge">{t("inquiry.required")}</span>
      {children}
    </span>
  );
}

function OptionalLabel({ children }: { children: React.ReactNode }) {
  const { t } = useTranslation();
  return (
    <span className="inquiry-form__label-text">
      <span
        className="inquiry-form__badge inquiry-form__badge--spacer"
        aria-hidden
      >
        {t("inquiry.required")}
      </span>
      {children}
    </span>
  );
}

const Inquiry: React.FC = () => {
  const { t } = useTranslation();
  const [privacyModalOpen, setPrivacyModalOpen] = useState(false);
  const [iscaptchaChecked, setIscaptchaChecked] = useState(false);
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const recaptchaRef = useRef<{ reset: () => void } | null>(null);

  const [form] = Form.useForm<FieldType>();

  const sendInquiryRequest = (values: FieldType) => {
    const payload = {
      companyName: values.companyName,
      departmentName: values.departmentName ?? "",
      name: values.name,
      mail: values.mail,
      telephone: values.telephone,
      content: buildMailContent(values, t),
      inquiryType: values.inquiryType,
      remarks: values.remarks ?? "",
      captchaToken,
    };

    fetch("/send-mail", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(payload),
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error("Network response was not ok");
        }
        return response.json();
      })
      .then((data) => {
        console.log("Success:", data);
        Modal.success({
          title: t("inquiry.success_title"),
          content: t("inquiry.success_msg"),
        });
        form.resetFields();
        setIscaptchaChecked(false);
        setCaptchaToken(null);
        recaptchaRef.current?.reset();
      })
      .catch((err) => {
        console.error("Error:", err);
        Modal.error({
          title: t("inquiry.fail_title"),
          content: t("inquiry.fail_msg"),
        });
      });
  };

  const onFinish: FormProps<FieldType>["onFinish"] = (values) => {
    sendInquiryRequest(values);
  };

  const onFinishFailed: FormProps<FieldType>["onFinishFailed"] = (
    errorInfo
  ) => {
    console.log("Failed:", errorInfo);
  };

  const handleCaptchaChange = (token: string | null) => {
    if (token !== null) {
      setIscaptchaChecked(true);
    }
    setCaptchaToken(token);
  };

  return (
    <div className="subpage subpage--inquiry">
      <PageMeta path="/inquiry" {...PAGE_META["/inquiry"]} />
      <SubpageTitle titleJa="お問い合わせ" titleEn="contact" as="h1" />

      <FaqStickyLink />
      <section className="subpage-section" aria-label="お問い合わせフォーム">
        <p className="inquiry-intro">{t("inquiry.intro")}</p>
        <Space
          direction="vertical"
          style={{ width: "100%", marginTop: "0" }}
          align="center"
          wrap
          className="inquiry-form-wrap"
        >
          <Form<FieldType>
            form={form}
            name="inquiry"
            layout="horizontal"
            labelCol={{ xs: { span: 24 }, sm: { flex: "260px" } }}
            wrapperCol={{ xs: { span: 24 }, sm: { flex: "1" } }}
            labelAlign="left"
            colon={false}
            onFinish={onFinish}
            onFinishFailed={onFinishFailed}
            className="inquiry-form"
            autoComplete="off"
            initialValues={{ inquiryType: "quote" }}
          >
            <Form.Item<FieldType>
              label={<RequiredLabel>{t("inquiry.company_name")}</RequiredLabel>}
              name="companyName"
              rules={[
                { required: true, message: `${t("inquiry.company_name")}` },
              ]}
            >
              <Input maxLength={100} placeholder="" />
            </Form.Item>

            <Form.Item<FieldType>
              label={<OptionalLabel>{t("inquiry.department_name")}</OptionalLabel>}
              name="departmentName"
            >
              <Input maxLength={100} placeholder="" />
            </Form.Item>

            <Form.Item<FieldType>
              label={<RequiredLabel>{t("inquiry.name")}</RequiredLabel>}
              name="name"
              rules={[
                { required: true, message: `${t("inquiry.name")}` },
              ]}
            >
              <Input maxLength={50} placeholder="" />
            </Form.Item>

            <Form.Item<FieldType>
              label={<RequiredLabel>{t("inquiry.email")}</RequiredLabel>}
              name="mail"
              rules={[
                {
                  required: true,
                  message: `${t("inquiry.email")}`,
                },
                {
                  type: "email",
                  message: `${t("inquiry.email")}`,
                },
              ]}
            >
              <Input maxLength={50} placeholder="" />
            </Form.Item>

            <Form.Item<FieldType>
              label={<RequiredLabel>{t("inquiry.phone")}</RequiredLabel>}
              name="telephone"
              rules={[
                {
                  required: true,
                  message: `${t("inquiry.phone")}`,
                },
                {
                  pattern: /^[0-9]{10,15}$/,
                  message: `${t("inquiry.phone")}`,
                },
              ]}
            >
              <Input maxLength={20} placeholder="" />
            </Form.Item>

            <Form.Item<FieldType>
              label={<RequiredLabel>{t("inquiry.type")}</RequiredLabel>}
              name="inquiryType"
              rules={[
                {
                  required: true,
                  message: `${t("inquiry.type")}`,
                },
              ]}
            >
              <Radio.Group className="inquiry-form__radios">
                {INQUIRY_TYPES.map((opt) => (
                  <Radio key={opt.value} value={opt.value}>
                    {t(opt.labelKey)}
                  </Radio>
                ))}
              </Radio.Group>
            </Form.Item>

            <Form.Item<FieldType>
              label={<OptionalLabel>{t("inquiry.remarks")}</OptionalLabel>}
              name="remarks"
            >
              <Input.TextArea maxLength={500} rows={5} placeholder="" />
            </Form.Item>

            <div className="recaptcha">
              <RecaptchaComponent
                ref={recaptchaRef}
                onCaptchaChange={handleCaptchaChange}
              />
            </div>

            <div className="inquiry-form__privacy-wrap">
              <Form.Item<FieldType>
                name="privacyAgree"
                valuePropName="checked"
                rules={[
                  {
                    validator: async (_, checked) => {
                      if (checked !== true) {
                        return Promise.reject(
                          new Error(t("inquiry.privacy_agree"))
                        );
                      }
                      return Promise.resolve();
                    },
                  },
                ]}
                style={{ marginBottom: 8 }}
              >
                <Checkbox>
                  {t("inquiry.privacy_agree")}
                </Checkbox>
              </Form.Item>
              <button
                type="button"
                className="inquiry-form__privacy-link"
                onClick={() => setPrivacyModalOpen(true)}
              >
                {t("footer.privacy_policy")}
              </button>
            </div>

            <Form.Item className="inquiry-form__submit-wrap">
              <Button
                type="primary"
                htmlType="submit"
                className="inquiry-form__submit-btn"
                disabled={!iscaptchaChecked}
              >
                {t("buttons.submit")}
              </Button>
            </Form.Item>
          </Form>
        </Space>
      </section>

      <Modal
        title={t("footer.privacy_title")}
        open={privacyModalOpen}
        onOk={() => setPrivacyModalOpen(false)}
        onCancel={() => setPrivacyModalOpen(false)}
        width={720}
        footer={[
          <Button
            key="close"
            type="primary"
            onClick={() => setPrivacyModalOpen(false)}
          >
            {t("buttons.close")}
          </Button>,
        ]}
      >
        <div style={{ maxHeight: "60vh", overflowY: "auto" }}>
          <PrivacyPolicyContent />
        </div>
      </Modal>
    </div>
  );
};

export default Inquiry;
